import { ethers } from 'ethers';

// EIP-6963 Provider Discovery
let _announced = null;  // cache only when non-empty

/**
 * Pick MetaMask provider from EIP-6963 announced providers cache
 * This avoids OKX hijacking window.ethereum
 */
function _pickMetaMaskFromAnnounced() {
    if (!_announced || _announced.length === 0) return null;
    
    // Prefer exact rdns first, then name contains
    const mm =
        _announced.find(d => d?.info?.rdns === 'io.metamask') ||
        _announced.find(d => (d?.info?.name || '').toLowerCase().includes('metamask'));
    
    return mm?.provider || null;
}

/**
 * Pick Rainbow Wallet provider from EIP-6963 announced providers cache
 * This provides more accurate detection than window.ethereum
 */
function _pickRainbowFromAnnounced() {
    if (!_announced || _announced.length === 0) return null;
    
    // Prefer exact rdns first, then name contains
    const rainbow =
        _announced.find(d => d?.info?.rdns === 'me.rainbow') ||
        _announced.find(d => (d?.info?.name || '').toLowerCase().includes('rainbow'));
    
    return rainbow?.provider || null;
}

/**
 * Deduplicate array by a key function
 */
function uniqBy(arr, key) {
    const seen = new Set();
    const out = [];
    for (const x of arr) {
        const k = key(x);
        if (!seen.has(k)) {
            seen.add(k);
            out.push(x);
        }
    }
    return out;
}

/**
 * Discover EIP-6963 providers (standardized wallet discovery)
 * More robust: longer timeout, double request, no empty cache
 */
export async function discoverEip6963Providers(timeoutMs = 1500) {
    // If we previously found wallets, reuse; but don't cache empties
    if (_announced && _announced.length > 0) return _announced;
    
    const found = [];
    
    const onAnnounce = (ev) => {
        const d = ev.detail;
        if (d?.provider && typeof d.provider.request === 'function') {
            found.push(d);
        }
    };
    
    window.addEventListener('eip6963:announceProvider', onAnnounce);
    
    // Ask wallets to announce (do it twice to catch slow injectors)
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    setTimeout(() => window.dispatchEvent(new Event('eip6963:requestProvider')), 200);
    
    // Wait a bit longer—some extensions are slow
    await new Promise(resolve => setTimeout(resolve, timeoutMs));
    
    window.removeEventListener('eip6963:announceProvider', onAnnounce);
    
    const unique = uniqBy(found, (d) => d.info?.uuid ?? d.info?.rdns ?? d.info?.name ?? Math.random().toString());
    
    // Cache only if we actually saw something
    if (unique.length > 0) _announced = unique;
    
    return unique;
}


// WalletConnect v2 (works with MetaMask, Coinbase, Rainbow, etc.)
let walletConnectProvider = null;

/**
 * Connect using WalletConnect v2
 * This works on all mobile browsers and avoids deep linking issues
 * Supports Trust Wallet, OKX Wallet, Rainbow Wallet, and other WalletConnect-compatible wallets
 */
export async function connectWithWalletConnect(projectId) {
    try {
        // Dynamically import WalletConnect to avoid loading if not needed
        // Use named export (package doesn't default-export the class)
        const { EthereumProvider } = await import('@walletconnect/ethereum-provider');
        
        if (!projectId) {
            throw new Error('WalletConnect Project ID is required. Please set REACT_APP_WALLETCONNECT_PROJECT_ID in your environment variables. Get a Project ID at https://cloud.walletconnect.com');
        }

        // Initialize WalletConnect with metadata
        walletConnectProvider = await EthereumProvider.init({
            projectId: projectId,
            chains: [421614, 11155111], // Arbitrum Sepolia, Ethereum Sepolia
            optionalChains: [421614, 11155111],
            showQrModal: true, // Desktop shows QR; mobile deep-links to the app
            metadata: {
                name: 'Confidential Balances',
                description: 'Confidential token transfer application',
                url: window.location.origin,
                icons: [`${window.location.origin}/favicon.ico`]
            },
            methods: [
                'eth_sendTransaction',
                'eth_signTransaction',
                'eth_sign',
                'personal_sign',
                'eth_signTypedData',
                'eth_signTypedData_v4',
                'wallet_switchEthereumChain',
                'wallet_addEthereumChain'
            ],
            events: ['accountsChanged', 'chainChanged', 'disconnect']
        });

        // Open wallet app / QR modal
        // Users can select from Trust Wallet, OKX Wallet, Rainbow Wallet, or any other WalletConnect-compatible wallet
        await walletConnectProvider.connect();

        // Wrap with ethers v5
        const provider = new ethers.providers.Web3Provider(walletConnectProvider, 'any');
        const signer = provider.getSigner();
        const accounts = await provider.listAccounts();
        const network = await provider.getNetwork();

        return {
            provider,
            signer,
            accounts,
            network,
            walletConnect: walletConnectProvider
        };
    } catch (error) {
        console.error('WalletConnect connection error:', error);
        throw error;
    }
}

/**
 * Disconnect WalletConnect
 */
export async function disconnectWalletConnect() {
    if (walletConnectProvider) {
        try {
            await walletConnectProvider.disconnect();
            walletConnectProvider = null;
        } catch (error) {
            console.error('Error disconnecting WalletConnect:', error);
        }
    }
}

/**
 * Get WalletConnect provider instance
 */
export function getWalletConnectProvider() {
    return walletConnectProvider;
}

// MetaMask SDK
let metaMaskSDK = null;
let metaMaskProvider = null;

/**
 * Connect using MetaMask SDK
 * Direct link to MetaMask app, no in-app browser required
 */
export async function connectWithMetaMaskSDK() {
    try {
        // Dynamically import MetaMask SDK
        const MetaMaskSDK = (await import('@metamask/sdk')).default;
        
        // Initialize MetaMask SDK
        metaMaskSDK = new MetaMaskSDK({
            dappMetadata: {
                name: 'Stabletrust',
                url: window.location.origin
            },
            // Mobile linking options
            preferDesktop: false,
            checkInstallationImmediately: false,
            // Enable logging for debugging
            logging: {
                sdk: false,
                remote: false
            }
        });

        // Get EIP-1193 provider
        metaMaskProvider = metaMaskSDK.getProvider();
        
        if (!metaMaskProvider) {
            throw new Error('MetaMask SDK provider not available');
        }

        // Request accounts
        const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });

        if (!accounts || accounts.length === 0) {
            throw new Error('No accounts returned from MetaMask');
        }

        // Wrap with ethers
        const provider = new ethers.providers.Web3Provider(metaMaskProvider, 'any');
        const signer = provider.getSigner();
        const network = await provider.getNetwork();

        return {
            provider,
            signer,
            accounts,
            network,
            metaMaskSDK: metaMaskSDK,
            metaMaskProvider: metaMaskProvider
        };
    } catch (error) {
        console.error('MetaMask SDK connection error:', error);
        throw error;
    }
}

/**
 * Get MetaMask SDK provider
 */
export function getMetaMaskSDKProvider() {
    return metaMaskProvider;
}

/**
 * Check if WalletConnect is available
 */
export function isWalletConnectAvailable() {
    return typeof window !== 'undefined';
}

/**
 * Check if MetaMask SDK is available
 */
export function isMetaMaskSDKAvailable() {
    return typeof window !== 'undefined';
}

/**
 * Check if MetaMask extension is installed
 */
export function isMetaMaskInstalled() {
    if (typeof window === 'undefined' || !window.ethereum) {
        return false;
    }
    
    // Check if MetaMask is available
    if (window.ethereum.isMetaMask) {
        return true;
    }
    
    // Check if multiple providers are detected
    if (window.ethereum.providers) {
        return window.ethereum.providers.some(provider => provider.isMetaMask);
    }
    
    return false;
}


/**
 * Check if Privy wallet is installed
 * Note: Privy is typically a web-based wallet that doesn't require an extension
 * but we can check for Privy provider injection
 * Privy uses WalletConnect, so it requires a WalletConnect Project ID to be configured
 */
export function isPrivyInstalled() {
    if (typeof window === 'undefined') {
        return false;
    }
    
    // Check if Privy provider is directly injected
    if (window.ethereum && window.ethereum.isPrivy) {
        return true;
    }
    
    // Check if multiple providers are detected
    if (window.ethereum && window.ethereum.providers) {
        if (window.ethereum.providers.some(provider => provider.isPrivy)) {
            return true;
        }
    }
    
    // Privy uses WalletConnect, so it requires a WalletConnect Project ID
    // Check if the project ID is configured in environment variables
    const walletConnectProjectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
    if (!walletConnectProjectId || walletConnectProjectId.trim() === '') {
        return false;
    }
    
    // If WalletConnect Project ID is available, Privy can work through WalletConnect
    return true;
}

/**
 * Check if Trust Wallet extension is installed
 */
export function isTrustWalletInstalled() {
    if (typeof window === 'undefined') {
        return false;
    }
    
    // Check for Trust Wallet via window.ethereum
    if (window.ethereum) {
        // Check if it's Trust Wallet
        if (window.ethereum.isTrust || window.ethereum.isTrustWallet) {
            return true;
        }
        
        // Check if multiple providers are detected
        if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
            return window.ethereum.providers.some(provider => 
                provider.isTrust || provider.isTrustWallet
            );
        }
    }
    
    // Also check window.trustwallet (some versions inject here)
    if (window.trustwallet) {
        return true;
    }
    
    return false;
}

/**
 * Check if OKX Wallet extension is installed
 */
export function isOKXWalletInstalled() {
    if (typeof window === 'undefined') {
        return false;
    }
    
    // Check window.okxwallet first (OKX injects here)
    if (window.okxwallet) {
        return true;
    }
    
    if (window.ethereum) {
        // Check if primary provider is OKX
        if (window.ethereum.isOKExWallet) {
            return true;
        }
        
        // Check providers array
        if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
            return window.ethereum.providers.some(provider => 
                provider.isOKExWallet || provider._okxwallet
            );
        }
    }
    
    return false;
}

/**
 * Check if Coinbase Wallet extension is installed
 */
export function isCoinbaseWalletInstalled() {
    if (typeof window === 'undefined') {
        return false;
    }

    if (window.coinbaseWalletExtension) {
        return true;
    }

    if (window.walletLinkExtension) {
        return true;
    }

    if (window.ethereum) {
        if (window.ethereum.isCoinbaseWallet || window.ethereum.isWalletLink || window.ethereum.isCoinbaseBrowser) {
            return true;
        }

        if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
            return window.ethereum.providers.some(provider =>
                provider.isCoinbaseWallet || provider.isWalletLink || provider.isCoinbaseBrowser
            );
        }
    }

    return false;
}

/**
 * Check if Rainbow Wallet extension is installed
 */
export function isRainbowWalletInstalled() {
    if (typeof window === 'undefined') {
        return false;
    }
    
    try {
        if (window.ethereum) {
            // Check window.ethereum.isRainbow first (safer, doesn't trigger window.rainbow initialization)
            if (window.ethereum.isRainbow) {
                return true;
            }
            
            if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
                return window.ethereum.providers.some(provider => 
                    provider.isRainbow
                );
            }
        }
        
        // Check window.rainbow last (may trigger initialization, so wrap in try-catch)
        if (window.rainbow) {
            return true;
        }
    } catch (error) {
        // Rainbow Wallet's inpage script might throw errors during initialization
        // Log but don't crash - just return false
        console.warn('Error checking Rainbow Wallet installation:', error);
        return false;
    }
    
    return false;
}

/**
 * Get Trust Wallet provider - STRICT: Only returns Trust Wallet, no fallbacks
 */
export function getTrustWalletProvider() {
    if (typeof window === 'undefined') {
        return null;
    }
    
    // Check window.trustwallet first (some versions inject here)
    if (window.trustwallet) {
        // Verify it's actually Trust Wallet
        if (window.trustwallet.isTrust || window.trustwallet.isTrustWallet) {
            return window.trustwallet;
        }
    }
    
    // Check window.ethereum - STRICTLY Trust Wallet only
    if (window.ethereum) {
        // Single provider - must be Trust Wallet and NOT other wallets
        if ((window.ethereum.isTrust || window.ethereum.isTrustWallet) &&
            !window.ethereum.isMetaMask &&
            !window.ethereum.isOKExWallet &&
            !window.ethereum.isRainbow) {
            return window.ethereum;
        }
        
        // Multiple providers - find Trust Wallet STRICTLY
        if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
            return window.ethereum.providers.find(provider => 
                (provider.isTrust || provider.isTrustWallet) &&
                !provider.isMetaMask &&
                !provider.isOKExWallet &&
                !provider.isRainbow
            ) || null;
        }
    }
    
    // No Trust Wallet found - return null, DO NOT fallback
    return null;
}

/**
 * Get OKX Wallet provider - STRICT: Only returns OKX Wallet, no fallbacks
 */
export function getOKXWalletProvider() {
    if (typeof window === 'undefined') {
        return null;
    }
    
    // Check window.okxwallet first (OKX injects here)
    if (window.okxwallet) {
        // OKX might not always set isOKExWallet on window.okxwallet
        // If it has request method, it's likely OKX
        if (typeof window.okxwallet.request === 'function') {
            console.log('Found OKX Wallet via window.okxwallet');
            return window.okxwallet;
        }
    }
    
    // Check window.ethereum - STRICTLY OKX Wallet only
    if (window.ethereum) {
        // Multiple providers - find OKX Wallet STRICTLY (check this first)
        if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
            console.log('Checking providers array for OKX Wallet...', {
                providers: window.ethereum.providers.map(p => ({
                    isOKExWallet: p.isOKExWallet,
                    isMetaMask: p.isMetaMask,
                    hasRequest: typeof p.request === 'function'
                }))
            });
            
            const okxProvider = window.ethereum.providers.find(provider => 
                (provider.isOKExWallet || provider._okxwallet) &&
                !provider.isMetaMask &&
                !provider.isTrust &&
                !provider.isTrustWallet &&
                !provider.isRainbow
            );
            
            if (okxProvider) {
                console.log('Found OKX Wallet in providers array');
                return okxProvider;
            }
        }
        
        // Single provider - must be OKX Wallet and NOT other wallets
        if (window.ethereum.isOKExWallet &&
            !window.ethereum.isMetaMask &&
            !window.ethereum.isTrust &&
            !window.ethereum.isTrustWallet &&
            !window.ethereum.isRainbow) {
            console.log('Found OKX Wallet as primary window.ethereum');
            return window.ethereum;
        }
    }
    
    // No OKX Wallet found - return null, DO NOT fallback
    console.log('No OKX Wallet provider found');
    return null;
}

/**
 * Get Coinbase Wallet provider - STRICT: Only returns Coinbase Wallet, no fallbacks
 */
export function getCoinbaseWalletProvider() {
    if (typeof window === 'undefined') {
        return null;
    }

    if (window.coinbaseWalletExtension && typeof window.coinbaseWalletExtension.request === 'function') {
        return window.coinbaseWalletExtension;
    }

    if (window.walletLinkExtension && typeof window.walletLinkExtension.request === 'function') {
        return window.walletLinkExtension;
    }

    if (window.ethereum) {
        if ((window.ethereum.isCoinbaseWallet || window.ethereum.isWalletLink || window.ethereum.isCoinbaseBrowser) &&
            typeof window.ethereum.request === 'function') {
            return window.ethereum;
        }

        if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
            const coinbaseProvider = window.ethereum.providers.find(provider =>
                (provider.isCoinbaseWallet || provider.isWalletLink || provider.isCoinbaseBrowser) &&
                !provider.isMetaMask &&
                !provider.isOKExWallet &&
                !provider.isTrust &&
                !provider.isTrustWallet &&
                !provider.isRainbow
            );

            if (coinbaseProvider) {
                return coinbaseProvider;
            }
        }
    }

    return null;
}

/**
 * Get Rainbow Wallet provider - STRICT: Only returns Rainbow Wallet, no fallbacks
 * Note: Rainbow Wallet may set isMetaMask for compatibility, so we allow it if isRainbow is also set
 */
export function getRainbowWalletProvider() {
    if (typeof window === 'undefined') {
        return null;
    }
    
    try {
        // First: try EIP-6963-announced providers (from cache). This provides more accurate detection.
        const rainbowFrom6963 = _pickRainbowFromAnnounced();
        if (rainbowFrom6963) {
            console.log('Found Rainbow Wallet via EIP-6963 discovery');
            return rainbowFrom6963;
        }
        
        // Check window.ethereum (safer, doesn't trigger window.rainbow initialization)
        if (window.ethereum) {
            // Single provider - must be Rainbow Wallet
            // Note: Rainbow may set isMetaMask for compatibility, so we check isRainbow first
            if (window.ethereum.isRainbow &&
                !window.ethereum.isOKExWallet &&
                !window.ethereum.isTrust &&
                !window.ethereum.isTrustWallet) {
                return window.ethereum;
            }
            
            // Multiple providers - find Rainbow Wallet STRICTLY
            if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
                const rainbowProvider = window.ethereum.providers.find(provider => 
                    provider.isRainbow &&
                    !provider.isOKExWallet &&
                    !provider.isTrust &&
                    !provider.isTrustWallet
                );
                if (rainbowProvider) {
                    return rainbowProvider;
                }
            }
        }
        
        // Check window.rainbow last (may trigger initialization, so wrap in try-catch)
        // Only access if window.rainbow exists and has the expected properties
        if (window.rainbow) {
            try {
                // Use optional chaining to avoid triggering initialization errors
                if (window.rainbow.isRainbow || (typeof window.rainbow.request === 'function')) {
                    return window.rainbow;
                }
            } catch (error) {
                // Rainbow Wallet's inpage script might throw errors during property access
                console.warn('Error accessing window.rainbow properties:', error);
            }
        }
    } catch (error) {
        // Rainbow Wallet's inpage script might throw errors during initialization
        // Log but don't crash - just return null
        console.warn('Error getting Rainbow Wallet provider:', error);
        return null;
    }
    
    // No Rainbow Wallet found - return null, DO NOT fallback
    return null;
}

/**
 * Connect using Coinbase Wallet extension (desktop) - STRICT: No fallbacks
 */
export async function connectWithCoinbaseWallet() {
    const ethereum = getCoinbaseWalletProvider();

    if (!ethereum) {
        throw new Error('Coinbase Wallet extension is not installed. Please install the Coinbase Wallet browser extension.');
    }

    const isCoinbase = ethereum.isCoinbaseWallet || ethereum.isWalletLink || ethereum.isCoinbaseBrowser ||
        ethereum === window.coinbaseWalletExtension ||
        ethereum === window.walletLinkExtension ||
        (String(ethereum).toLowerCase().includes('coinbase') && !ethereum.isMetaMask);

    if (!isCoinbase) {
        throw new Error('Error: Detected wallet is not Coinbase Wallet. Please ensure Coinbase Wallet extension is installed and selected.');
    }

    if (ethereum.isMetaMask && !ethereum.isCoinbaseWallet) {
        throw new Error('Error: Detected wallet is MetaMask, not Coinbase Wallet. Please ensure only Coinbase Wallet is selected.');
    }

    if (ethereum.isOKExWallet || ethereum.isTrust || ethereum.isTrustWallet || ethereum.isRainbow) {
        throw new Error('Error: Detected wallet is not Coinbase Wallet. Please ensure only Coinbase Wallet is selected.');
    }

    try {
        const accounts = await ethereum.request({ method: 'eth_requestAccounts' });

        if (!accounts || accounts.length === 0) {
            throw new Error('No accounts returned from Coinbase Wallet. Please make sure your Coinbase Wallet extension is unlocked.');
        }

        const provider = new ethers.providers.Web3Provider(ethereum, 'any');
        const signer = provider.getSigner();
        const network = await provider.getNetwork();

        return {
            provider,
            signer,
            accounts,
            network
        };
    } catch (error) {
        console.error('Coinbase Wallet connection error:', error);
        if (error.code === 4001) {
            throw new Error('Connection to Coinbase Wallet was rejected. Please approve the connection in Coinbase Wallet.');
        }
        throw error;
    }
}

/**
 * Connect using Trust Wallet extension (desktop) - STRICT: No fallbacks
 */
export async function connectWithTrustWallet() {
    const ethereum = getTrustWalletProvider();
    
    if (!ethereum) {
        throw new Error('Trust Wallet extension is not installed. Please install the Trust Wallet browser extension.');
    }
    
    // STRICT VERIFICATION: Ensure this is actually Trust Wallet
    if (!ethereum.isTrust && !ethereum.isTrustWallet) {
        throw new Error('Error: Detected wallet is not Trust Wallet. Please ensure Trust Wallet extension is installed and selected.');
    }
    
    // Verify it's NOT another wallet
    if (ethereum.isMetaMask || ethereum.isOKExWallet || ethereum.isRainbow) {
        throw new Error('Error: Detected wallet is not Trust Wallet. Please ensure only Trust Wallet is selected.');
    }
    
    try {
        const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
        
        if (!accounts || accounts.length === 0) {
            throw new Error('No accounts returned from Trust Wallet. Please make sure your Trust Wallet extension is unlocked.');
        }
        
        const provider = new ethers.providers.Web3Provider(ethereum, 'any');
        const signer = provider.getSigner();
        const network = await provider.getNetwork();
        
        return {
            provider,
            signer,
            accounts,
            network
        };
    } catch (error) {
        console.error('Trust Wallet connection error:', error);
        // Re-throw with more user-friendly message if it's a user rejection
        if (error.code === 4001) {
            throw new Error('Connection to Trust Wallet was rejected. Please approve the connection in Trust Wallet.');
        }
        throw error;
    }
}

/**
 * Connect using OKX Wallet extension (desktop) - STRICT: No fallbacks
 */
export async function connectWithOKXWallet() {
    const ethereum = getOKXWalletProvider();
    
    if (!ethereum) {
        throw new Error('OKX Wallet extension is not installed. Please install the OKX Wallet browser extension.');
    }
    
    // Log provider details for debugging
    console.log('OKX Wallet provider check:', {
        isOKExWallet: ethereum.isOKExWallet,
        isMetaMask: ethereum.isMetaMask,
        isTrust: ethereum.isTrust,
        isTrustWallet: ethereum.isTrustWallet,
        hasRequest: typeof ethereum.request === 'function',
        providerString: String(ethereum),
        isWindowOkxwallet: ethereum === window.okxwallet
    });
    
    // STRICT VERIFICATION: Ensure this is actually OKX Wallet
    // OKX might be from window.okxwallet (which may not have isOKExWallet set)
    // or from window.ethereum.providers (which should have isOKExWallet)
    const isOKX = ethereum.isOKExWallet || 
                  ethereum === window.okxwallet ||
                  ethereum._okxwallet ||
                  (String(ethereum).toLowerCase().includes('okx') && !ethereum.isMetaMask);
    
    if (!isOKX) {
        throw new Error('Error: Detected wallet is not OKX Wallet. Please ensure OKX Wallet extension is installed and selected.');
    }
    
    // Verify it's NOT another wallet (only if we have positive indicators)
    if (ethereum.isMetaMask && !ethereum.isOKExWallet) {
        throw new Error('Error: Detected wallet is MetaMask, not OKX Wallet. Please ensure only OKX Wallet is selected.');
    }
    if (ethereum.isTrust || ethereum.isTrustWallet) {
        throw new Error('Error: Detected wallet is Trust Wallet, not OKX Wallet. Please ensure only OKX Wallet is selected.');
    }
    if (ethereum.isRainbow) {
        throw new Error('Error: Detected wallet is Rainbow Wallet, not OKX Wallet. Please ensure only OKX Wallet is selected.');
    }
    
    try {
        const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
        
        if (!accounts || accounts.length === 0) {
            throw new Error('No accounts returned from OKX Wallet. Please make sure your OKX Wallet extension is unlocked.');
        }
        
        const provider = new ethers.providers.Web3Provider(ethereum, 'any');
        const signer = provider.getSigner();
        const network = await provider.getNetwork();
        
        return {
            provider,
            signer,
            accounts,
            network
        };
    } catch (error) {
        console.error('OKX Wallet connection error:', error);
        // Re-throw with more user-friendly message if it's a user rejection
        if (error.code === 4001) {
            throw new Error('Connection to OKX Wallet was rejected. Please approve the connection in OKX Wallet.');
        }
        throw error;
    }
}

/**
 * Connect using Rainbow Wallet extension (desktop) - STRICT: No fallbacks
 * Note: Rainbow Wallet may set isMetaMask for compatibility, so we allow it if isRainbow is also set
 */
export async function connectWithRainbowWallet() {
    const ethereum = getRainbowWalletProvider();
    
    if (!ethereum) {
        throw new Error('Rainbow Wallet extension is not installed. Please install the Rainbow Wallet browser extension.');
    }
    
    // STRICT VERIFICATION: Ensure this is actually Rainbow Wallet
    // Check isRainbow first - Rainbow may set isMetaMask for compatibility
    if (!ethereum.isRainbow) {
        throw new Error('Error: Detected wallet is not Rainbow Wallet. Please ensure Rainbow Wallet extension is installed and selected.');
    }
    
    // Verify it's NOT another wallet
    // Note: Rainbow Wallet may set isMetaMask for compatibility, so we allow isMetaMask if isRainbow is also set
    // We only reject if it's clearly another wallet (OKX, Trust, etc.)
    if (ethereum.isOKExWallet || ethereum.isTrust || ethereum.isTrustWallet) {
        throw new Error('Error: Detected wallet is not Rainbow Wallet. Please ensure only Rainbow Wallet is selected.');
    }
    
    try {
        const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
        
        if (!accounts || accounts.length === 0) {
            throw new Error('No accounts returned from Rainbow Wallet. Please make sure your Rainbow Wallet extension is unlocked.');
        }
        
        const provider = new ethers.providers.Web3Provider(ethereum, 'any');
        const signer = provider.getSigner();
        const network = await provider.getNetwork();
        
        return {
            provider,
            signer,
            accounts,
            network
        };
    } catch (error) {
        console.error('Rainbow Wallet connection error:', error);
        // Re-throw with more user-friendly message if it's a user rejection
        if (error.code === 4001) {
            throw new Error('Connection to Rainbow Wallet was rejected. Please approve the connection in Rainbow Wallet.');
        }
        throw error;
    }
}

/**
 * Stellar Wallets Kit-backed connection helpers.
 *
 * The app previously talked to each Stellar wallet (Freighter, Hana, ...)
 * through its native API. That path has been fully replaced with
 * https://stellarwalletskit.dev/, which exposes a single API across every
 * supported Stellar wallet (Freighter, Hana, xBull, Albedo, Lobstr, Rabet,
 * WalletConnect, Hot, Klever, OneKey, Bitget, Ledger, Trezor).
 *
 * The legacy `connectWithFreighter` / `connectWithHana` exports are kept as
 * thin wrappers so existing callers continue to work — they now open the
 * kit's unified auth modal rather than talking to the wallet directly.
 */
import { FREIGHTER_ID } from '@creit.tech/stellar-wallets-kit/modules/freighter';
import { HANA_ID } from '@creit.tech/stellar-wallets-kit/modules/hana';
import {
    openStellarWalletsAuthModal,
    restoreStellarWalletsSession,
    disconnectStellarWallet,
    StellarWalletsKit,
    ensureKitInitialized,
} from './stellarWalletsKit';

/**
 * Open the Stellar Wallets Kit auth modal so the user can pick any supported
 * Stellar wallet. Returns `{ publicKey, origin, walletId }`.
 */
export async function connectWithStellarWalletsKit() {
    const { address, origin, walletId } = await openStellarWalletsAuthModal();
    if (!address) {
        throw new Error('No address returned from the Stellar wallet');
    }
    return { publicKey: address, origin, walletId };
}

/**
 * Legacy helper: used to ask Freighter directly for access. Now it opens the
 * kit's auth modal (which already highlights Freighter when it is installed).
 */
export async function connectWithFreighter() {
    ensureKitInitialized();
    try {
        StellarWalletsKit.setWallet(FREIGHTER_ID);
    } catch (_) { /* fall through to authModal if setWallet fails */ }
    const { publicKey, origin, walletId } = await connectWithStellarWalletsKit();
    return { publicKey, origin, walletId };
}

/**
 * Legacy helper: used to talk to `window.hanaWallet.stellar` directly. Now
 * opens the kit's auth modal with Hana preselected.
 */
export async function connectWithHana() {
    ensureKitInitialized();
    try {
        StellarWalletsKit.setWallet(HANA_ID);
    } catch (_) { /* fall through to authModal if setWallet fails */ }
    const { publicKey, origin, walletId } = await connectWithStellarWalletsKit();
    return { publicKey, origin, walletId };
}

/**
 * Try to restore a previously-connected Stellar wallet without showing UI.
 */
export async function restoreStellarWallet() {
    const restored = await restoreStellarWalletsSession();
    if (!restored) return null;
    return {
        publicKey: restored.address,
        origin: restored.origin,
        walletId: restored.walletId,
    };
}

/**
 * Disconnect whichever Stellar wallet is active through the kit.
 */
export async function disconnectStellarWalletSession() {
    await disconnectStellarWallet();
}

/**
 * Availability is now managed entirely by the kit. The custom availability
 * check is kept as a stub that always reports both wallets as "available"
 * because the kit itself handles per-wallet availability inside its modal.
 */
export async function getAvailableWallets() {
    return { freighter: true, hana: true };
}

/**
 * Kept for any legacy callers. The kit now owns presence detection.
 */
export function isFreighterExtensionPresent() {
    return typeof window !== 'undefined';
}

export function isHanaWalletPresent() {
    return typeof window !== 'undefined';
}

/**
 * Get MetaMask provider - STRICT: Only returns MetaMask, no fallbacks
 * CRITICAL: When multiple wallets are installed, window.ethereum might point to OKX
 * We must ALWAYS check providers array first and ignore primary window.ethereum
 */
export function getMetaMaskProvider() {
    // First: try EIP-6963-announced providers (from cache). This avoids OKX hijacking.
    const mmFrom6963 = _pickMetaMaskFromAnnounced();
    if (mmFrom6963) {
        console.log('Found MetaMask via EIP-6963 discovery');
        return mmFrom6963;
    }
    
    if (typeof window === 'undefined' || !window.ethereum) {
        return null;
    }
    
    // CRITICAL: If multiple providers exist, ALWAYS check providers array first
    // The primary window.ethereum might be OKX even if providers array exists
    if (window.ethereum.providers && Array.isArray(window.ethereum.providers) && window.ethereum.providers.length > 1) {
        console.log('Multiple providers detected, searching providers array for MetaMask...', {
            providers: window.ethereum.providers.map(p => ({
                isMetaMask: p.isMetaMask,
                isOKExWallet: p.isOKExWallet,
                isTrust: p.isTrust,
                isTrustWallet: p.isTrustWallet,
                name: p.toString()
            }))
        });
        
        // Filter STRICTLY for MetaMask - must pass ALL checks
        const validProviders = window.ethereum.providers.filter(provider => {
            // MUST NOT be Rainbow (check this FIRST, before isMetaMask check)
            // Rainbow might set isMetaMask for compatibility
            if (provider.isRainbow) {
                console.log('Rejecting provider: isRainbow=true');
                return false;
            }
            
            // Check if provider is window.rainbow (even if isRainbow flag isn't set)
            // Use try-catch to avoid triggering Rainbow's initialization errors
            try {
                if (window.rainbow && provider === window.rainbow) {
                    console.log('Rejecting provider: is window.rainbow');
                    return false;
                }
            } catch (error) {
                // Ignore errors from Rainbow Wallet's inpage script
                console.warn('Error checking window.rainbow:', error);
            }
            
            // MUST be MetaMask
            if (!provider.isMetaMask) {
                return false;
            }
            
            // MUST NOT be OKX (even if it claims to be MetaMask)
            if (provider.isOKExWallet) {
                console.log('Rejecting provider: isOKExWallet=true');
                return false;
            }
            
            // MUST NOT be Trust
            if (provider.isTrust || provider.isTrustWallet) {
                return false;
            }
            
            // Check for OKX-specific properties
            if (provider._state?.isOKExWallet || provider._okxwallet) {
                console.log('Rejecting provider: has OKX-specific properties');
                return false;
            }
            
            // Check provider string representation
            const providerStr = String(provider).toLowerCase();
            if (providerStr.includes('okx') || providerStr.includes('okex')) {
                console.log('Rejecting provider: string contains OKX');
                return false;
            }
            
            // Check provider string representation for Rainbow
            if (providerStr.includes('rainbow')) {
                console.log('Rejecting provider: string contains Rainbow');
                return false;
            }
            
            // Check for MetaMask-specific properties (positive indicators)
            const hasMetaMaskProps = provider._metamask !== undefined || 
                                    provider.selectedAddress !== undefined ||
                                    (provider._state && provider._state.isUnlocked !== undefined);
            
            if (!hasMetaMaskProps) {
                console.log('Rejecting provider: missing MetaMask-specific properties');
                return false;
            }
            
            return true;
        });
        
        console.log('Filtered MetaMask providers:', validProviders.length);
        
        if (validProviders.length === 0) {
            console.log('No valid MetaMask provider found in providers array');
            return null;
        }
        
        // If we found exactly one valid MetaMask provider, return it
        if (validProviders.length === 1) {
            console.log('Found MetaMask provider in providers array');
            return validProviders[0];
        }
        
        // If multiple providers pass all checks, prefer the one with strongest MetaMask indicators
        const realMetaMask = validProviders.find(provider => provider._metamask) ||
                            validProviders.find(provider => provider.selectedAddress !== undefined) ||
                            validProviders[0];
        
        console.log('Selected MetaMask provider from multiple matches');
        return realMetaMask;
    }
    
    // Single provider or no providers array - check primary window.ethereum
    // BUT: If OKX or Rainbow is installed, window.ethereum might be OKX/Rainbow even without providers array
    // So we must be EXTRA strict
    
    // MUST check isRainbow FIRST (before isMetaMask) - Rainbow might set isMetaMask for compatibility
    if (window.ethereum.isRainbow) {
        console.log('Primary window.ethereum is Rainbow, not MetaMask');
        return null; // This is Rainbow, not MetaMask
    }
    
    // Check for Rainbow-specific properties even if isRainbow is false
    // Use try-catch to avoid triggering Rainbow's initialization errors
    try {
        if (window.rainbow && window.ethereum === window.rainbow) {
            console.log('Primary window.ethereum is window.rainbow');
            return null;
        }
    } catch (error) {
        // Ignore errors from Rainbow Wallet's inpage script
        console.warn('Error checking window.rainbow:', error);
    }
    
    // MUST check isOKExWallet (before isMetaMask)
    if (window.ethereum.isOKExWallet) {
        console.log('Primary window.ethereum is OKX, not MetaMask');
        return null; // This is OKX, not MetaMask
    }
    
    // Check for OKX-specific properties even if isOKExWallet is false
    if (window.ethereum._okxwallet || window.ethereum._state?.isOKExWallet) {
        console.log('Primary window.ethereum has OKX properties');
        return null;
    }
    
    // Check string representation for OKX
    const providerStr = String(window.ethereum).toLowerCase();
    if (providerStr.includes('okx') || providerStr.includes('okex')) {
        console.log('Primary window.ethereum string contains OKX');
        return null;
    }
    
    // Check string representation for Rainbow
    if (providerStr.includes('rainbow')) {
        console.log('Primary window.ethereum string contains Rainbow');
        return null;
    }
    
    // Now check if it's MetaMask
    if (window.ethereum.isMetaMask === true && 
        !window.ethereum.isTrust && 
        !window.ethereum.isTrustWallet &&
        !window.ethereum.isRainbow) {
        // Additional verification: check for MetaMask-specific properties
        if (window.ethereum._metamask || window.ethereum.selectedAddress !== undefined) {
            console.log('Primary window.ethereum is MetaMask');
            return window.ethereum;
        }
    }
    
    // No MetaMask found - return null, DO NOT fallback
    console.log('No MetaMask provider found');
    return null;
}


/**
 * Connect using a specific EIP-1193 provider directly
 * This ensures we use the exact provider we selected, not window.ethereum
 */
export async function connectWithSpecificEip1193Provider(eip1193) {
    // Minimal safety checks
    if (!eip1193 || typeof eip1193.request !== 'function') {
        throw new Error('Invalid provider');
    }
    
    const accounts = await eip1193.request({ method: 'eth_requestAccounts' });
    
    if (!accounts || accounts.length === 0) {
        throw new Error('No accounts returned');
    }
    
    const provider = new ethers.providers.Web3Provider(eip1193, 'any');
    const signer = provider.getSigner();
    const network = await provider.getNetwork();
    
    return {
        provider,
        signer,
        accounts,
        network
    };
}

/**
 * Connect using MetaMask extension
 */
export async function connectWithMetaMask() {
    const ethereum = getMetaMaskProvider();
    
    if (!ethereum) {
        throw new Error('MetaMask is not installed');
    }
    
    try {
        // Request accounts
        const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
        
        if (!accounts || accounts.length === 0) {
            throw new Error('No accounts returned from MetaMask');
        }
        
        // Wrap with ethers
        const provider = new ethers.providers.Web3Provider(ethereum, 'any');
        const signer = provider.getSigner();
        const network = await provider.getNetwork();
        
        return {
            provider,
            signer,
            accounts,
            network
        };
    } catch (error) {
        console.error('MetaMask connection error:', error);
        throw error;
    }
}


/**
 * Connect using Privy wallet
 * Privy typically uses WalletConnect under the hood
 */
export async function connectWithPrivy(projectId) {
    // Privy can work through WalletConnect
    // We'll use WalletConnect for Privy connections
    return connectWithWalletConnect(projectId);
}

